import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { Button } from '../../components/UI/Button/Button';
import { useApi } from '../../hooks/useApi';
import { useStores } from '../../hooks/useStore';
import {
  AudioBlockData,
  Course,
  CourseModule,
  HeadingBlockData,
  ImageBlockData,
  Lesson,
  LessonBlock,
  LessonBlockData,
  ParagraphBlockData,
  VideoBlockData,
} from '../../models/courses';

import { BlockWrapper } from './components/blocks/BlockWrapper';
import { VideoBlock } from './components/blocks/VideoBlock';
import { ParagraphBlock } from './components/blocks/ParagraphBlock';
import { ImageBlock } from './components/blocks/ImageBlock';
import { AudioBlock } from './components/blocks/AudioBlock';
import { HeadingBlock } from './components/blocks/HeadingBlock';

interface LessonEditorProps {
  course: Course;
  module: CourseModule;
  lesson: Lesson;
  onBack: () => void;
  onSaved: () => void;
  onBackToCourses: () => void;
  onBackToModules: () => void;
}

type BlockType = LessonBlock['type'];

const BLOCK_LABELS: Record<BlockType, string> = {
  video: 'Видео',
  paragraph: 'Текст',
  image: 'Изображение',
  audio: 'Аудио',
  heading: 'Заголовок',
};

const BLOCK_TYPE_OPTIONS: { value: BlockType; label: string }[] = [
  { value: 'paragraph', label: 'Текст' },
  { value: 'heading', label: 'Заголовок' },
  { value: 'image', label: 'Изображение' },
  { value: 'video', label: 'Видео' },
  { value: 'audio', label: 'Аудио' },
];

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

function createEmptyBlock(type: BlockType): LessonBlock {
  const id = generateId();
  switch (type) {
    case 'video':
      return {
        id,
        type,
        data: { provider: 'youtube', video_id: '', caption: '' },
      };
    case 'paragraph':
      return { id, type, data: { text: '' } };
    case 'image':
      return { id, type, data: { src: '', alt: '', caption: '' } };
    case 'audio':
      return { id, type, data: { src: '', caption: '' } };
    case 'heading':
      return { id, type, data: { level: 2, text: '' } };
    default: {
      const _exhaustive: never = type;
      throw new Error(`Unknown block type: ${_exhaustive}`);
    }
  }
}

const LessonEditor: React.FC<LessonEditorProps> = ({
  course,
  module,
  lesson,
  onBack,
  onSaved,
  onBackToCourses,
  onBackToModules,
}) => {
  const api = useApi();
  const store = useStores();

  const [title, setTitle] = useState<string>(lesson.title);
  const [durationMinutes, setDurationMinutes] = useState<number>(
    lesson.duration_minutes ?? 0,
  );
  const [isPreview, setIsPreview] = useState<boolean>(
    lesson.is_preview ?? false,
  );
  const [blocks, setBlocks] = useState<LessonBlock[]>(
    lesson.content?.blocks ?? [],
  );
  const [newBlockType, setNewBlockType] = useState<BlockType>('paragraph');
  const [saving, setSaving] = useState<boolean>(false);

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value, 10);
    setDurationMinutes(isNaN(val) ? 0 : val);
  };

  const handleNewBlockTypeChange = (e: SelectChangeEvent<BlockType>) => {
    setNewBlockType(e.target.value as BlockType);
  };

  const handleAddBlock = () => {
    setBlocks((prev) => [...prev, createEmptyBlock(newBlockType)]);
  };

  const handleUpdateBlock = (index: number, data: LessonBlockData) => {
    setBlocks((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], data };
      return updated;
    });
  };

  const handleDeleteBlock = (index: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index));
  };

  const handleMoveBlock = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= blocks.length) {
      return;
    }
    setBlocks((prev) => {
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated;
    });
  };

  const handleUploadImage = async (file: File): Promise<string> => {
    return api.courses.uploadImage(file);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      store.notificationsStore.setError('Название урока обязательно');
      return;
    }

    try {
      setSaving(true);
      await api.courses.updateLesson(
        {
          title,
          duration_minutes: durationMinutes,
          is_preview: isPreview,
          content: { version: 1, blocks },
        },
        lesson.id,
      );
      onSaved();
    } catch {
      store.notificationsStore.setError('Ошибка при сохранении урока');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <h2
        style={{
          color: 'var(--black)',
          fontFamily: 'var(--font-h)',
          fontSize: '24px',
          lineHeight: '28px',
          marginBottom: '24px',
        }}
      >
        <span
          onClick={onBackToCourses}
          style={{ cursor: 'pointer', opacity: 0.5 }}
        >
          Курсы
        </span>
        {' > '}
        <span
          onClick={onBackToModules}
          style={{ cursor: 'pointer', opacity: 0.5 }}
        >
          {course.title}
        </span>
        {' > '}
        <span onClick={onBack} style={{ cursor: 'pointer', opacity: 0.5 }}>
          {module.title}
        </span>
        {' > '}
        {lesson.title}
      </h2>

      {/* Мета-данные урока */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          maxWidth: 700,
          mb: 4,
          mt: 3,
        }}
      >
        <TextField
          label="Название"
          required
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={saving}
        />
        <TextField
          label="Длительность"
          type="number"
          fullWidth
          value={durationMinutes}
          onChange={handleDurationChange}
          disabled={saving}
          InputProps={{
            endAdornment: <InputAdornment position="end">мин</InputAdornment>,
            inputProps: { min: 0 },
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isPreview}
              onChange={(e) => setIsPreview(e.target.checked)}
              disabled={saving}
            />
          }
          label="Превью (доступен без покупки)"
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Блоки контента */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Контент урока
      </Typography>

      {blocks.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, fontStyle: 'italic' }}
        >
          Блоки отсутствуют. Добавьте первый блок ниже.
        </Typography>
      )}

      {blocks.map((block, index) => (
        <BlockWrapper
          key={block.id}
          label={BLOCK_LABELS[block.type]}
          onDelete={() => handleDeleteBlock(index)}
          onUp={() => handleMoveBlock(index, 'up')}
          onDown={() => handleMoveBlock(index, 'down')}
          disabledUp={index === 0}
          disabledDown={index === blocks.length - 1}
          disabled={saving}
        >
          {block.type === 'video' && (
            <VideoBlock
              data={block.data as VideoBlockData}
              onChange={(d) => handleUpdateBlock(index, d)}
              disabled={saving}
            />
          )}
          {block.type === 'paragraph' && (
            <ParagraphBlock
              data={block.data as ParagraphBlockData}
              onChange={(d) => handleUpdateBlock(index, d)}
              disabled={saving}
            />
          )}
          {block.type === 'image' && (
            <ImageBlock
              data={block.data as ImageBlockData}
              onChange={(d) => handleUpdateBlock(index, d)}
              onUpload={handleUploadImage}
              disabled={saving}
            />
          )}
          {block.type === 'audio' && (
            <AudioBlock
              data={block.data as AudioBlockData}
              onChange={(d) => handleUpdateBlock(index, d)}
              disabled={saving}
            />
          )}
          {block.type === 'heading' && (
            <HeadingBlock
              data={block.data as HeadingBlockData}
              onChange={(d) => handleUpdateBlock(index, d)}
              disabled={saving}
            />
          )}
        </BlockWrapper>
      ))}

      {/* Добавить блок */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1, mb: 4 }}>
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={saving}>
          <InputLabel id="new-block-type-label">Тип блока</InputLabel>
          <Select<BlockType>
            labelId="new-block-type-label"
            value={newBlockType}
            label="Тип блока"
            onChange={handleNewBlockTypeChange}
          >
            {BLOCK_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button appearance="auth" onClick={handleAddBlock} disabled={saving}>
          + Добавить блок
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Сохранить / Отмена */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button appearance="auth" onClick={handleSave} disabled={saving}>
          {saving ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            'Сохранить'
          )}
        </Button>
        <Button appearance="back" onClick={onBack} disabled={saving}>
          Отмена
        </Button>
      </Box>
    </Box>
  );
};

export default LessonEditor;
